import React from "react";
import { Box, Tooltip } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { StatusTone, useToneColor } from "./StatusPill";

interface StatusRingProps {
  value: number;
  total: number;
  size?: number;
  label?: string;
  /** Worst state of the group (same scale as every status dot); defaults to a count-based colour. */
  tone?: StatusTone;
}

/** Circular progress of bots in range, coloured by the group's worst state (red > gold > green). */
export const StatusRing: React.FC<StatusRingProps> = ({ value, total, size = 44, label, tone }) => {
  const theme = useTheme();
  const resolveTone = useToneColor();
  const stroke = size >= 40 ? 4 : 3;
  const r = (size - stroke) / 2 - 1;
  const c = 2 * Math.PI * r;
  const frac = total > 0 ? Math.min(1, value / total) : 0;
  const color = tone
    ? resolveTone(tone)
    : value >= total && total > 0
    ? theme.palette.success.main
    : value === 0
    ? theme.palette.error.main
    : theme.palette.warning.main;

  return (
    <Tooltip title={label ?? `${value} of ${total} bots in range`}>
      <Box sx={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden style={{ overflow: "visible", display: "block" }}>
          {/* None in range: no arc (a round cap would draw a stray dot), the track itself turns red. */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            stroke={frac > 0 ? "rgba(255,255,255,0.10)" : alpha(color, 0.4)}
            strokeWidth={stroke}
            fill="none"
          />
          {frac > 0 && (
            <circle
              cx={size / 2}
              cy={size / 2}
              r={r}
              stroke={color}
              strokeWidth={stroke}
              fill="none"
              strokeLinecap="round"
              strokeDasharray={`${c * frac} ${c}`}
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
              style={{ filter: `drop-shadow(0 0 4px ${alpha(color, 0.55)})` }}
            />
          )}
        </svg>
        <Box
          component="span"
          sx={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
            fontSize: size >= 40 ? "0.75rem" : "0.6875rem",
            fontWeight: 750,
            color: "text.primary",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {value}/{total}
        </Box>
      </Box>
    </Tooltip>
  );
};

export default StatusRing;
